import React from 'react';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useLoggingEvent } from '@reverbdotcom/commons/src/useLoggingEvent';
import { core_apimessages_Money } from '../schemas/rql';
import { CanadianKlarnaCallout } from './CanadianKlarnaCallout';
import { isPayLaterSupported, isPayOverTimeSupported } from '../lib/klarnaOnSiteMessaging';
import { KlarnaPayLaterCallout } from './KlarnaPayLaterCallout';
import { KlarnaCreditPlacement } from './KlarnaCreditPlacement';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';

type Money = Pick<core_apimessages_Money, 'amountCents' | 'currency'>;

interface Props {
  amount: Money;
  textOnly?: boolean;
}

export function RegionalKlarnaCallout({
  amount,
  textOnly = false,
}: Props) {
  const user = useUser();
  const showPayLater = isPayLaterSupported(user.countryCode) && isExperimentEnabled(user, experiments.KLARNA_PAY_LATER);

  useCalloutMountedLoggingEvent(amount);

  if (user.countryCode === 'CA') {
    return (
      <CanadianKlarnaCallout
        amount={amount}
        textOnly={textOnly}
      />
    );
  }

  if (showPayLater) {
    return (
      <KlarnaPayLaterCallout
        amount={amount}
        textOnly={textOnly}
      />
    );
  }

  if (isPayOverTimeSupported(user.countryCode)) {
    return (
      <KlarnaCreditPlacement
        amountCents={amount.amountCents}
        textOnly={textOnly}
      />
    );
  }

  return null;
}

function useCalloutMountedLoggingEvent(amount: Money) {
  const { fiveDigitLocale } = useUser();

  useLoggingEvent({
    eventName: 'klarna_callout.mounted',
    data: {
      amount_cents: amount?.amountCents,
      user_locale: fiveDigitLocale,
    },
    shouldLog: true,
  });
}
